import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  FacebookLogo, TwitterLogo, LinkedinLogo, YoutubeLogo,
  Plus, Trash, Copy, Sparkle, Video, Link as LinkIcon, X, Check,
} from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "../../lib/api";
import StoryboardPanel from "./StoryboardPanel";
import { assetUrl } from "../../lib/assetUrl";

const PLATFORM_META = {
  facebook: { label: "Facebook Page", icon: FacebookLogo, tokenLabel: "Page Access Token", helper: "Generated from Meta Developer App > Graph API Explorer with pages_manage_posts.", needsPageId: true, charLimit: 63000 },
  twitter:  { label: "Twitter / X",    icon: TwitterLogo,  tokenLabel: "Bearer Token",        helper: "X Developer Portal > Project > Keys & Tokens > Bearer Token (Basic tier).",         needsPageId: false, charLimit: 280 },
  linkedin: { label: "LinkedIn",       icon: LinkedinLogo, tokenLabel: "OAuth Access Token", helper: "LinkedIn Marketing Developer Platform > 3-legged OAuth token with w_member_social.", needsPageId: true, charLimit: 3000 },
  youtube:  { label: "YouTube",        icon: YoutubeLogo,  tokenLabel: "OAuth Access Token", helper: "Google Cloud > YouTube Data API v3 > OAuth 2.0 client with youtube.upload scope.", needsPageId: false, charLimit: 5000 },
};

export default function SocialPanel() {
  const [subTab, setSubTab] = useState("composer");
  const [platforms, setPlatforms] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [connectModal, setConnectModal] = useState(null); // platform key

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, c, d] = await Promise.all([
        api.get("/admin/social/platforms"),
        api.get("/admin/social/feature-candidates"),
        api.get("/admin/social/drafts"),
      ]);
      setPlatforms(p.data);
      setCandidates(c.data);
      setDrafts(d.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Poll drafts every 5s if any are "generating".
  useEffect(() => {
    const hasGen = drafts.some((d) => d.status === "generating");
    if (!hasGen) return undefined;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get("/admin/social/drafts");
        setDrafts(data);
      } catch (_e) { /* ignore poll errors */ }
    }, 5000);
    return () => clearInterval(t);
  }, [drafts]);

  if (loading) return <div className="text-muted-foreground" data-testid="admin-social-loading">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="admin-social-panel">
      <div className="flex gap-1 border-b hairline">
        {[
          ["composer", "Composer"],
          ["storyboard", "Storyboard"],
          ["connections", `Connections (${platforms.filter((p) => p.connected).length}/${platforms.length})`],
          ["drafts", `Drafts (${drafts.length})`],
        ].map(([id, label]) => (
          <button
            key={id}
            onClick={() => setSubTab(id)}
            data-testid={`admin-social-subtab-${id}`}
            className={`px-4 py-2 text-sm -mb-px border-b-2 transition ${subTab === id ? "border-[hsl(var(--primary))] text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}
          >{label}</button>
        ))}
      </div>

      {subTab === "composer" && (
        <Composer
          candidates={candidates}
          generating={generating}
          setGenerating={setGenerating}
          onCreated={() => { setSubTab("drafts"); load(); }}
        />
      )}

      {subTab === "storyboard" && <StoryboardPanel />}

      {subTab === "connections" && (
        <Connections platforms={platforms} onConnect={(p) => setConnectModal(p)} onRefresh={load} />
      )}

      {subTab === "drafts" && (
        <Drafts drafts={drafts} platforms={platforms} onRefresh={load} />
      )}

      {connectModal && (
        <ConnectModal
          platform={connectModal}
          onClose={() => setConnectModal(null)}
          onSaved={() => { setConnectModal(null); load(); }}
        />
      )}
    </div>
  );
}

function Composer({ candidates, generating, setGenerating, onCreated }) {
  const [topic, setTopic] = useState("");
  const [featureSlug, setFeatureSlug] = useState("");
  const [videoMode, setVideoMode] = useState("none");
  const [externalUrl, setExternalUrl] = useState("");
  const [soraDuration, setSoraDuration] = useState(8);
  const [slideshowDuration, setSlideshowDuration] = useState(30);

  const pickRandom = () => {
    if (!candidates.length) return;
    const c = candidates[Math.floor(Math.random() * candidates.length)];
    setFeatureSlug(c.slug);
    setTopic("");
  };

  const submit = async () => {
    setGenerating(true);
    try {
      await api.post("/admin/social/draft", {
        topic: topic.trim() || null,
        feature_slug: featureSlug || null,
        video_mode: videoMode,
        external_video_url: videoMode === "external" ? externalUrl.trim() : null,
        sora_duration: parseInt(soraDuration, 10),
        slideshow_duration: parseInt(slideshowDuration, 10),
      });
      toast.success(videoMode === "sora"
        ? "Caption ready - Sora 2 video is rendering (2-5 min)."
        : videoMode === "slideshow"
          ? "Caption ready - slideshow video rendering."
          : "Draft created.");
      onCreated?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setGenerating(false); }
  };

  const sel = candidates.find((c) => c.slug === featureSlug);

  return (
    <div className="space-y-5" data-testid="admin-social-composer">
      <div className="card-flat p-6">
        <h3 className="font-serif text-2xl mb-1">Feature of the day</h3>
        <p className="text-xs text-muted-foreground mb-4">
          Pick a feature (or type a custom topic). Estima drafts the caption + video and saves it as a Draft you can copy or publish.
        </p>

        <div className="grid sm:grid-cols-2 gap-4">
          <label className="text-sm block">
            <span className="block text-xs text-muted-foreground mb-1">Pick a feature</span>
            <div className="flex gap-2">
              <select
                value={featureSlug}
                onChange={(e) => { setFeatureSlug(e.target.value); setTopic(""); }}
                className="flex-1 bg-background border hairline px-3 py-2 text-sm"
                data-testid="admin-social-feature-select"
              >
                <option value="">— Auto-pick today's feature —</option>
                {candidates.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
              </select>
              <button onClick={pickRandom} className="btn-ghost px-3 py-2 text-xs" data-testid="admin-social-random">
                Random
              </button>
            </div>
            {sel && <div className="text-[11px] text-muted-foreground mt-1.5">{sel.hook}</div>}
          </label>

          <label className="text-sm block">
            <span className="block text-xs text-muted-foreground mb-1">…or type a custom topic</span>
            <input
              type="text"
              value={topic}
              onChange={(e) => { setTopic(e.target.value); setFeatureSlug(""); }}
              placeholder="e.g. Bengaluru rent surge 2026"
              className="w-full bg-background border hairline px-3 py-2 text-sm"
              data-testid="admin-social-custom-topic"
            />
          </label>
        </div>

        <div className="mt-5">
          <div className="text-xs text-muted-foreground mb-2">Video source</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
            {[
              ["none", "No video", "Caption only - admin posts text manually."],
              ["sora", "Sora 2 (AI)", "Cinematic clip generated by Sora 2 (4 / 8 / 12 sec)."],
              ["slideshow", "Slideshow", "Title card MP4 via ffmpeg (up to 60 sec)."],
              ["external", "External URL", "Paste a YouTube / Vimeo / MP4 link."],
            ].map(([k, label, hint]) => (
              <label key={k} className={`border hairline px-3 py-3 cursor-pointer text-sm ${videoMode === k ? "border-[hsl(var(--secondary))] bg-[hsl(var(--muted))]" : "hover:bg-[hsl(var(--muted))]"}`} data-testid={`admin-social-videomode-${k}`}>
                <div className="flex items-center gap-2">
                  <input type="radio" name="vmode" checked={videoMode === k} onChange={() => setVideoMode(k)} />
                  <span className="font-medium">{label}</span>
                </div>
                <div className="text-[11px] text-muted-foreground mt-1">{hint}</div>
              </label>
            ))}
          </div>
        </div>

        {videoMode === "sora" && (
          <div className="mt-4 text-sm">
            <label className="block">
              <span className="block text-xs text-muted-foreground mb-1">Sora 2 clip length (seconds)</span>
              <select value={soraDuration} onChange={(e) => setSoraDuration(e.target.value)} className="bg-background border hairline px-3 py-2 text-sm" data-testid="admin-social-sora-duration">
                <option value={4}>4 sec (fastest)</option>
                <option value={8}>8 sec (default)</option>
                <option value={12}>12 sec (max)</option>
              </select>
            </label>
            <div className="text-[11px] text-muted-foreground mt-1">Note: Sora 2 caps single-clip at 12 seconds. For longer cuts, use Slideshow.</div>
          </div>
        )}

        {videoMode === "slideshow" && (
          <div className="mt-4 text-sm">
            <label className="block">
              <span className="block text-xs text-muted-foreground mb-1">Slideshow length (seconds)</span>
              <input type="number" min={4} max={60} value={slideshowDuration} onChange={(e) => setSlideshowDuration(e.target.value)} className="w-28 bg-background border hairline px-3 py-2 text-sm" data-testid="admin-social-slideshow-duration" />
            </label>
          </div>
        )}

        {videoMode === "external" && (
          <div className="mt-4 text-sm">
            <label className="block">
              <span className="block text-xs text-muted-foreground mb-1">External video URL (YouTube / Vimeo / MP4)</span>
              <input type="url" value={externalUrl} onChange={(e) => setExternalUrl(e.target.value)} placeholder="https://www.youtube.com/watch?v=…" className="w-full bg-background border hairline px-3 py-2 text-sm" data-testid="admin-social-external-url" />
            </label>
          </div>
        )}

        <div className="flex justify-end mt-6">
          <button
            onClick={submit}
            disabled={generating || (videoMode === "external" && !externalUrl.trim())}
            className="btn-primary px-5 py-2.5 text-sm inline-flex items-center gap-2 disabled:opacity-60"
            data-testid="admin-social-generate"
          >
            <Sparkle size={14} weight="duotone" /> {generating ? "Generating…" : "Generate draft"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Connections({ platforms, onConnect, onRefresh }) {
  const disconnect = async (platform) => {
    if (!window.confirm(`Disconnect ${PLATFORM_META[platform].label}?`)) return;
    try {
      await api.delete(`/admin/social/connect/${platform}`);
      toast.success("Disconnected.");
      onRefresh?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="grid sm:grid-cols-2 gap-4" data-testid="admin-social-connections">
      {platforms.map((p) => {
        const meta = PLATFORM_META[p.platform];
        const Icon = meta.icon;
        return (
          <div key={p.platform} className={`card-flat p-5 ${p.connected ? "border-[hsl(var(--secondary))]" : ""}`} data-testid={`admin-social-platform-${p.platform}`}>
            <div className="flex items-start gap-3 mb-3">
              <Icon size={28} weight="duotone" className="text-[hsl(var(--secondary))] shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="font-serif text-xl">{meta.label}</div>
                <div className="text-[10px] text-muted-foreground">char-limit per post: {meta.charLimit}</div>
              </div>
              {p.connected && <span className="text-[10px] uppercase tracking-wider text-[hsl(var(--secondary))]">Connected</span>}
            </div>
            {p.connected ? (
              <div className="text-sm space-y-1.5">
                <div className="text-muted-foreground">Account: <span className="text-foreground">{p.account_name || "—"}</span></div>
                <div className="text-[10px] text-muted-foreground">Since {p.connected_at ? new Date(p.connected_at).toLocaleDateString() : "—"}</div>
                <button onClick={() => disconnect(p.platform)} className="text-xs text-[hsl(var(--primary))] hover:text-foreground inline-flex items-center gap-1 mt-1" data-testid={`admin-social-disconnect-${p.platform}`}>
                  <Trash size={12} /> Disconnect
                </button>
              </div>
            ) : (
              <div>
                <p className="text-xs text-muted-foreground mb-3">{meta.helper}</p>
                <button onClick={() => onConnect(p.platform)} className="btn-primary px-3 py-2 text-xs inline-flex items-center gap-1" data-testid={`admin-social-connect-${p.platform}`}>
                  <Plus size={12} weight="bold" /> Connect {meta.label}
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ConnectModal({ platform, onClose, onSaved }) {
  const meta = PLATFORM_META[platform];
  const [accountName, setAccountName] = useState("");
  const [token, setToken] = useState("");
  const [pageId, setPageId] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!accountName.trim()) { toast.error("Account name required"); return; }
    if (!token.trim()) { toast.error("Access token required"); return; }
    setBusy(true);
    try {
      await api.post("/admin/social/connect", {
        platform,
        account_name: accountName.trim(),
        access_token: token.trim(),
        page_id: pageId.trim() || null,
      });
      toast.success(`${meta.label} connected.`);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="card-flat max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid={`admin-social-modal-${platform}`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-2xl">Connect {meta.label}</h3>
          <button onClick={onClose} className="btn-ghost p-1.5" data-testid="admin-social-modal-close"><X size={14} /></button>
        </div>
        <p className="text-xs text-muted-foreground mb-4">{meta.helper}</p>
        <div className="space-y-3">
          <label className="block text-sm">
            <span className="block text-xs text-muted-foreground mb-1">Account name / handle</span>
            <input value={accountName} onChange={(e) => setAccountName(e.target.value)} placeholder="@estima_india" className="w-full bg-background border hairline px-3 py-2 text-sm" data-testid="admin-social-account-name" />
          </label>
          <label className="block text-sm">
            <span className="block text-xs text-muted-foreground mb-1">{meta.tokenLabel}</span>
            <textarea value={token} onChange={(e) => setToken(e.target.value)} rows={3} placeholder="Paste token here…" className="w-full bg-background border hairline px-3 py-2 text-sm font-mono" data-testid="admin-social-token" />
          </label>
          {meta.needsPageId && (
            <label className="block text-sm">
              <span className="block text-xs text-muted-foreground mb-1">Page / Organization ID</span>
              <input value={pageId} onChange={(e) => setPageId(e.target.value)} placeholder="123456789" className="w-full bg-background border hairline px-3 py-2 text-sm font-mono" data-testid="admin-social-page-id" />
            </label>
          )}
        </div>
        <div className="text-[11px] text-muted-foreground bg-[hsl(var(--muted))] p-3 mt-4">
          <strong>Phase 1 - draft mode:</strong> the token is stored securely but live publishing is wired in the next release.
          Until then, Estima drafts the content and you copy it to the platform manually.
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="btn-ghost px-3 py-2 text-xs" data-testid="admin-social-modal-cancel">Cancel</button>
          <button onClick={save} disabled={busy} className="btn-primary px-3 py-2 text-xs inline-flex items-center gap-1 disabled:opacity-60" data-testid="admin-social-modal-save">
            <Check size={12} weight="bold" /> {busy ? "Saving…" : "Save connection"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Drafts({ drafts, platforms, onRefresh }) {
  if (!drafts.length) {
    return <div className="card-flat p-12 text-center text-muted-foreground text-sm" data-testid="admin-social-drafts-empty">
      No drafts yet. Head to <strong>Composer</strong> and generate today's feature post.
    </div>;
  }
  return (
    <div className="space-y-4" data-testid="admin-social-drafts">
      {drafts.map((d) => <DraftCard key={d.id} draft={d} platforms={platforms} onRefresh={onRefresh} />)}
    </div>
  );
}

function DraftCard({ draft, platforms, onRefresh }) {
  const [picked, setPicked] = useState(new Set());
  const togglePlat = (p) => {
    const s = new Set(picked);
    if (s.has(p)) s.delete(p); else s.add(p);
    setPicked(s);
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied.");
  };

  const remove = async () => {
    if (!window.confirm("Delete this draft?")) return;
    try {
      await api.delete(`/admin/social/drafts/${draft.id}`);
      toast.success("Deleted.");
      onRefresh?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const publish = async () => {
    if (picked.size === 0) { toast.error("Pick at least one platform"); return; }
    try {
      const { data } = await api.post(`/admin/social/drafts/${draft.id}/publish`, { platforms: [...picked] });
      const hasLive = (data.platforms_posted || []).some((x) => x.status === "live_pending");
      toast.success(hasLive
        ? "Logged - live publishing will run when the live-API release ships."
        : "Marked as drafted-copied for the selected platforms.");
      onRefresh?.();
      setPicked(new Set());
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const statusBadge = {
    draft: { tone: "muted", label: "DRAFT" },
    generating: { tone: "secondary", label: "GENERATING…" },
    ready: { tone: "secondary", label: "READY" },
    posted: { tone: "primary", label: "POSTED" },
    error: { tone: "primary", label: "ERROR" },
  }[draft.status] || { tone: "muted", label: draft.status };

  return (
    <div className="card-flat p-5" data-testid={`admin-social-draft-${draft.id}`}>
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div className="min-w-0">
          <div className="font-serif text-xl truncate">{draft.topic}</div>
          <div className="text-[10px] text-muted-foreground">{new Date(draft.created_at).toLocaleString("en-IN")}</div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className={`text-[10px] uppercase tracking-wider ${statusBadge.tone === "primary" ? "text-[hsl(var(--primary))]" : statusBadge.tone === "secondary" ? "text-[hsl(var(--secondary))]" : "text-muted-foreground"}`}>
            {statusBadge.label}
          </span>
          <button onClick={remove} className="btn-ghost p-1.5 text-[hsl(var(--primary))]" data-testid={`admin-social-draft-delete-${draft.id}`}><Trash size={14} /></button>
        </div>
      </div>

      {draft.error && (
        <div className="text-xs text-[hsl(var(--primary))] bg-[hsl(var(--muted))] p-2 mb-3">{draft.error}</div>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="space-y-3 text-sm">
          <CaptionBlock label="Long caption (FB / LI)" text={draft.long_caption} onCopy={copy} testid={`draft-long-${draft.id}`} />
          <CaptionBlock label="Short caption (X / Twitter)" text={draft.short_caption} onCopy={copy} testid={`draft-short-${draft.id}`} />
          {draft.hashtags?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Hashtags</div>
              <div className="flex flex-wrap gap-1">
                {draft.hashtags.map((h) => (
                  <button key={h} onClick={() => copy(`#${h}`)} className="bg-[hsl(var(--muted))] px-1.5 py-0.5 text-[10px] rounded-sm hover:bg-foreground hover:text-background" data-testid={`draft-hashtag-${h}`}>#{h}</button>
                ))}
                <button onClick={() => copy(draft.hashtags.map((h) => `#${h}`).join(" "))} className="text-[10px] text-[hsl(var(--secondary))] underline ml-1">Copy all</button>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-2">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Video · {draft.video_mode}</div>
          {draft.video_url && (
            <video src={assetUrl(draft.video_url)} controls className="w-full border hairline" data-testid={`draft-video-${draft.id}`} />
          )}
          {draft.external_video_url && !draft.video_url && (
            <a href={draft.external_video_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-[hsl(var(--secondary))] hover:underline" data-testid={`draft-external-${draft.id}`}>
              <LinkIcon size={14} /> {draft.external_video_url}
            </a>
          )}
          {draft.status === "generating" && (
            <div className="text-xs text-muted-foreground inline-flex items-center gap-2">
              <Video size={14} weight="duotone" className="animate-pulse" /> Rendering — this takes 30 s for slideshow, 2-5 min for Sora 2.
            </div>
          )}
          {draft.video_prompt && (
            <details className="text-xs text-muted-foreground">
              <summary className="cursor-pointer">Sora 2 video prompt</summary>
              <div className="p-2 bg-[hsl(var(--muted))] mt-1 whitespace-pre-wrap">{draft.video_prompt}</div>
            </details>
          )}
        </div>
      </div>

      <div className="border-t hairline mt-4 pt-4">
        <div className="text-xs text-muted-foreground mb-2">Publish to (Phase 1 logs the action; live posting ships next):</div>
        <div className="flex flex-wrap gap-2">
          {platforms.map((p) => {
            const meta = PLATFORM_META[p.platform];
            const Icon = meta.icon;
            const already = (draft.platforms_posted || []).find((x) => x.platform === p.platform);
            const isPicked = picked.has(p.platform);
            return (
              <button
                key={p.platform}
                onClick={() => togglePlat(p.platform)}
                className={`px-3 py-1.5 text-xs border hairline inline-flex items-center gap-1.5 ${isPicked ? "bg-[hsl(var(--secondary))] text-background border-[hsl(var(--secondary))]" : "hover:bg-[hsl(var(--muted))]"}`}
                data-testid={`draft-platform-toggle-${draft.id}-${p.platform}`}
              >
                <Icon size={12} weight="duotone" /> {meta.label}
                {already && <Check size={12} weight="bold" className="text-[hsl(var(--secondary))]" />}
              </button>
            );
          })}
          <button
            onClick={publish}
            disabled={picked.size === 0}
            className="btn-primary px-3 py-1.5 text-xs inline-flex items-center gap-1 disabled:opacity-60 ml-auto"
            data-testid={`draft-publish-${draft.id}`}
          >Mark posted ({picked.size})</button>
        </div>
      </div>
    </div>
  );
}

function CaptionBlock({ label, text, onCopy, testid }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</span>
        <button onClick={() => onCopy(text)} className="text-[10px] text-[hsl(var(--secondary))] hover:underline inline-flex items-center gap-1" data-testid={`${testid}-copy`}>
          <Copy size={11} /> Copy
        </button>
      </div>
      <div className="text-sm bg-[hsl(var(--muted))] p-3 whitespace-pre-wrap" data-testid={testid}>{text || "—"}</div>
    </div>
  );
}
